import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Share2, Play, User, Hash } from "lucide-react";
import { fetchEpisode } from "../lib/api";
import { formatDate } from "../lib/brand";
import SEO from "../components/SEO";

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function EpisodeDetail() {
  const { slug } = useParams();
  const [ep, setEp] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setErr(false);
    fetchEpisode(slug).then(setEp).catch(() => setErr(true));
  }, [slug]);

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: ep?.title, url: window.location.href });
      } else {
        await navigator.clipboard.writeText(window.location.href);
      }
    } catch {}
  };

  if (err) return (
    <div className="max-w-3xl mx-auto px-5 py-24 text-center">
      <div className="text-headline text-4xl">Episode not found.</div>
      <Link to="/episodes" className="btn-rust mt-6 inline-flex">Back to Episodes</Link>
    </div>
  );

  if (!ep) return <div className="py-24 text-center font-mono-ed uppercase tracking-widest text-[var(--muted)]">Loading…</div>;

  const ytSrc = ep.youtube_video_id ? `https://www.youtube.com/embed/${ep.youtube_video_id}?modestbranding=1&rel=0` : null;
  const spotifySrc = ep.spotify_episode_id ? `https://open.spotify.com/embed/episode/${ep.spotify_episode_id}?utm_source=generator&theme=0` : null;

  const jumpTo = (seconds) => {
    // Try to seek the YouTube iframe (requires postMessage API, best-effort).
    const ytIframe = document.querySelector("#ep-youtube");
    if (ytIframe) {
      ytIframe.src = `https://www.youtube.com/embed/${ep.youtube_video_id}?modestbranding=1&rel=0&start=${seconds}&autoplay=1`;
      ytIframe.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <article data-testid={`episode-detail-${ep.slug}`}>
      <SEO
        title={ep.title}
        description={ep.description}
        image={ep.hero_image}
        type="article"
        publishedAt={ep.publish_date}
      />

      {/* Top meta */}
      <div className="border-b-2 border-[var(--ink)]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-6 flex items-center justify-between">
          <Link to="/episodes" className="inline-flex items-center gap-2 text-sm font-display uppercase tracking-widest hover:text-[var(--rust)]" data-testid="back-to-episodes">
            <ArrowLeft size={14} /> Back to Episodes
          </Link>
          <button onClick={share} className="inline-flex items-center gap-2 text-sm font-display uppercase tracking-widest hover:text-[var(--rust)]" data-testid="episode-share-button">
            <Share2 size={14} /> Share
          </button>
        </div>
      </div>

      {/* Header */}
      <header className="border-b-2 border-[var(--ink)]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="tape">Ep. {ep.number}</span>
            {ep.featured && <span className="chip chip-active text-[0.65rem]">★ Featured</span>}
            <span className="text-kicker">{formatDate(ep.publish_date)}</span>
            <span className="text-kicker inline-flex items-center gap-1"><Clock size={12} /> {ep.duration}</span>
          </div>
          <h1 className="text-serif-headline text-4xl md:text-6xl lg:text-7xl">
            {ep.title}
          </h1>
          <p className="text-dek text-xl md:text-2xl mt-5 max-w-3xl">{ep.description}</p>
        </div>
      </header>

      {/* Players */}
      <section className="border-b-2 border-[var(--ink)] py-10 bg-[var(--paper-deep)]/30" data-testid="episode-players">
        <div className="max-w-5xl mx-auto px-5 md:px-8 grid md:grid-cols-5 gap-8">
          {ytSrc && (
            <div className="md:col-span-3" data-testid="youtube-player">
              <div className="text-kicker mb-3">Watch</div>
              <div className="relative aspect-video border-2 border-[var(--ink)] bg-[var(--ink)] overflow-hidden">
                <iframe id="ep-youtube"
                        src={ytSrc} title={`${ep.title} — YouTube`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full" />
              </div>
            </div>
          )}
          <div className={ytSrc ? "md:col-span-2" : "md:col-span-5"} data-testid="spotify-player">
            <div className="text-kicker mb-3">Listen</div>
            {spotifySrc ? (
              <iframe src={spotifySrc}
                      title={`${ep.title} — Spotify`}
                      width="100%" height="232"
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      className="border-2 border-[var(--ink)]" />
            ) : (
              <div className="border-2 border-dashed border-[var(--ink)] p-8 text-center">
                <Play size={28} className="mx-auto text-[var(--rust)]" />
                <p className="font-display uppercase tracking-widest text-sm mt-3">Audio coming soon</p>
                <p className="text-xs text-[var(--muted)] mt-2 font-mono-ed">Dropping on Spotify Monday.</p>
                <a href="https://open.spotify.com/show/unfilteredandonball" target="_blank" rel="noreferrer"
                   className="btn-ghost text-xs mt-4 inline-flex">
                  Follow on Spotify
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Chapters + Guest */}
      <section className="py-12 border-b-2 border-[var(--ink)]">
        <div className="max-w-5xl mx-auto px-5 md:px-8 grid md:grid-cols-3 gap-10">
          {/* Chapters */}
          {ep.chapters && ep.chapters.length > 0 && (
            <div className="md:col-span-2" data-testid="episode-chapters">
              <div className="text-kicker">Chapters</div>
              <h2 className="text-headline text-3xl md:text-4xl mt-1 mb-6">Jump in.</h2>
              <div className="divide-y divide-[var(--line)] border-y-2 border-[var(--ink)]">
                {ep.chapters.map((c, i) => (
                  <button key={i} onClick={() => jumpTo(c.time_seconds)}
                          className="w-full flex items-center gap-5 py-3 px-2 text-left hover:bg-[var(--paper-deep)]/40 transition"
                          data-testid={`chapter-${i}`}>
                    <span className="font-mono-ed text-xs text-[var(--rust)] w-14 shrink-0">{formatTime(c.time_seconds)}</span>
                    <span className="font-serif-ed text-base flex-1">{c.label}</span>
                    <Play size={14} className="text-[var(--muted)] shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Guest + Tags */}
          <aside className="space-y-6">
            {ep.guest_name && (
              <div className="sticker-card p-5" data-testid="episode-guest">
                <div className="text-kicker flex items-center gap-2"><User size={14} /> Guest</div>
                <h3 className="text-headline text-2xl mt-2">{ep.guest_name}</h3>
                {ep.guest_handle && (
                  <div className="font-mono-ed text-xs text-[var(--rust)] mt-1">{ep.guest_handle}</div>
                )}
                {ep.guest_bio && <p className="text-dek text-sm mt-3">{ep.guest_bio}</p>}
              </div>
            )}
            {ep.tags && ep.tags.length > 0 && (
              <div>
                <div className="text-kicker flex items-center gap-2"><Hash size={14} /> Tags</div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {ep.tags.map((t) => (
                    <span key={t} className="chip text-[0.65rem]">#{t}</span>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* Featured articles */}
      {ep.featured_articles && ep.featured_articles.length > 0 && (
        <section className="py-12 border-b-2 border-[var(--ink)]" data-testid="episode-featured-articles">
          <div className="max-w-5xl mx-auto px-5 md:px-8">
            <div className="flex items-end justify-between pb-4 mb-6 border-b-2 border-[var(--ink)]">
              <div>
                <div className="text-kicker">Featured in this episode</div>
                <h2 className="text-headline text-3xl md:text-4xl">Read the recaps.</h2>
              </div>
              <Link to="/editorial" className="nav-link hidden sm:inline">All Stories</Link>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {ep.featured_articles.map((a) => (
                <Link key={a.slug} to={`/editorial/${a.slug}`}
                      className="article-card block"
                      data-testid={`episode-related-${a.slug}`}>
                  <div className="overflow-hidden border-2 border-[var(--ink)] aspect-[16/10]">
                    <img src={a.hero_image} alt={a.title} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="pt-3">
                    <span className="tape text-[0.65rem]">{a.category}</span>
                    <h3 className="text-headline text-xl mt-3 art-title">{a.title}</h3>
                    <p className="text-dek mt-1 text-sm line-clamp-2">{a.dek}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
